import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useRef } from 'react';
import startCase from 'lodash/startCase';

import { createPortal } from 'react-dom';
import {
  setActiveFilter,
} from '../../../store/actions/collections.actions';
import {
  getActiveFilters,
  getAllCollections,
} from '../../../store/selectors/collections.selectors';
import { getUserDetails } from '../../../store/selectors/user.selectors';
import {
  filterCategoryToOrder as filterCategoryToOrderGlobalAPI,
  PLPNav as PLPNavGlobalAPI,
} from '../../../mock/globalAPI';
import Analytics from '../Analytics';
import { withCssModulesClassNames } from '../../../common/nextMigrationHelpers';

import { default as styles } from '../../../scss/components/AnchorNav.module.scss';
import getCurrencyCode from '../../../common/getCurrencyCode';
import { getHrefLang } from '../../../store/selectors/general.selectors';
import useStickElementOnScroll from '../../../hooks/useStickElementOnScroll';

const withCssModulesClassNamesHandler = withCssModulesClassNames(styles);

type Filters = keyof typeof filterCategoryToOrderGlobalAPI;

type FilterType = { available: boolean; category: Filters | 'Show All'; categoryDisplayName: string; order: number; };

type NavItem = {
  productCollectionItems: {
    filterCategories: Filters[]
  }[]
};

const FilterNav = () => {
  const navItems: NavItem[] = useSelector(getAllCollections);
  const activeFilters = useSelector(getActiveFilters);
  const userDetails = useSelector(getUserDetails);
  const hrefLang = useSelector(getHrefLang);
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const { shouldRenderPortal, portal } = useStickElementOnScroll(containerRef);

  useEffect(() => () => {
    dispatch(setActiveFilter(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setActiveFilters = (category: FilterType['category']) => {
    try {
      const { email = '' } = userDetails;
      if (category) {
        Analytics.enqueue({
          method: 'track',
          params: {
            event: 'Product Collections FilterNav',
            properties: {
              action: 'Filtered',
              currency: getCurrencyCode(hrefLang),
              email,
              filterName: category,
            },
          },
          location: Analytics.getLocation(),
        });
      }
    } catch (e) {
      /* nothing to do */
    }
    if (category === 'Show All') {
      return dispatch(setActiveFilter(null));
    }
    return dispatch(setActiveFilter(category));
  };

  const getAllFilterTypes = () => {
    const showAllFilter: FilterType = {
      available: true,
      category: 'Show All',
      categoryDisplayName: 'Show All',
      order: 0,
    };
    const collectionItemsFilters = [showAllFilter];
    const filterMap: {
      [key: string]: FilterType
    } = {
      'Show All': showAllFilter,
    };

    // Add all the unique collectionItems to a new array and filter out Lifestyle Cards
    navItems.forEach((navItem) => navItem.productCollectionItems.forEach(({ filterCategories }) => {
      if (filterCategories?.length) {
        filterCategories.forEach((filterCategory: Filters) => {
          if (filterMap[filterCategory]) return;
          const formattedFilterCategory = {
            available: true,
            category: filterCategory,
            categoryDisplayName: startCase(filterCategory),
            order: filterCategoryToOrderGlobalAPI[filterCategory] || 20,
          };
          filterMap[filterCategory] = formattedFilterCategory;
          collectionItemsFilters.push(formattedFilterCategory);
        });
      }
    }));
    // Order them
    return collectionItemsFilters.sort((a, b) => a.order - b.order);
  };

  const allFilterTypes = getAllFilterTypes();

  const renderFilterNav = () => allFilterTypes.length > 1
    && (
      <div
        className={withCssModulesClassNamesHandler('anchor-nav')}
      >
        <div className={withCssModulesClassNamesHandler('anchor-nav-scroll')}>
          <div className={withCssModulesClassNamesHandler('anchor-nav-items')}>
            <span className={withCssModulesClassNamesHandler('anchor-nav-copy')}>
              {PLPNavGlobalAPI.filter}
            </span>
            {allFilterTypes.map(({ category, categoryDisplayName }) => {
              const active = (category === 'Show All' && !activeFilters.length)
                || activeFilters.includes(category);
              // FIXME: Re-enable the disabled state action when we filter on multiple categories
              // https://app.hive.com/workspace/BPQTWhi9hPh6N6usf?projectId=tyfaBvJGy5gZbfXz4&actionId=mALKQYnCMtPpFHc7o
              // const disabled = category !== 'Show All' && activeFilters.length && !availableFilters[category];
              return (
                <div
                  key={category}
                  className={withCssModulesClassNamesHandler('selector', {
                    active,
                  })}
                  onClick={() => setActiveFilters(category)}
                >
                  {categoryDisplayName}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );

  return (
    <div ref={containerRef}>
      {renderFilterNav()}
      {shouldRenderPortal && portal && createPortal(renderFilterNav(), portal)}
    </div>
  );
};

export default FilterNav;
